/*----------------------------------
- MENU SHARED HELPERS
----------------------------------*/

import type {
    LandingHeaderDomainsNavItem,
    LandingHeaderNavPanelItem,
} from '@/client/catalogs/landing/pageContent';
import { formatCompactStat } from '@/client/pages/landing/lib/formatCompactStat';

/*----------------------------------
- CONSTANTS
----------------------------------*/

/** Small uppercase label above a menu group, desktop megamenus. */
export const MENU_SECTION_LABEL_CLASS = 'text-[11px] font-bold uppercase tracking-[0.16em] text-[#66696b]';

/** Same role inside the mobile accordions, where the ground is cream instead of white. */
export const MOBILE_MENU_SECTION_LABEL_CLASS =
    'px-1 pb-2 text-[10.5px] font-extrabold uppercase tracking-[0.14em] text-[#6a695e]';

/*----------------------------------
- HELPERS
----------------------------------*/

export const getDomainsMenuColumnItems = (
    menu: LandingHeaderDomainsNavItem,
    title: string,
): readonly LandingHeaderNavPanelItem[] => menu.columns.find((column) => column.title === title)?.items || [];

/** Counts are optional producer data: anything that is not a real non-negative number renders nothing. */
export const readMenuItemCount = (menuItem: LandingHeaderNavPanelItem): string | null => {
    if (typeof menuItem.count !== 'number' || !Number.isFinite(menuItem.count) || menuItem.count < 0) return null;
    return formatCompactStat(menuItem.count);
};

/**
 * A total is optional producer data too. A missing or zero figure drops its sentence
 * instead of printing "0 domains", which would read as an outage rather than as a
 * snapshot that has not loaded yet.
 */
export const readMenuTotal = (value: number | null | undefined): number | null =>
    typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null;

/** Totals are written out in full: the panel headline is the one place the exact size is stated. */
export const formatMenuTotal = (value: number): string => new Intl.NumberFormat('en-US').format(value);

/** The payload sends bare extensions (`ai`), and the menu reads them as endings (`.ai`). */
export const formatTldLabel = (extension: string): string =>
    extension.startsWith('.') ? extension : `.${extension}`;
